/**
 * Billing is a calendar period, and a calendar period is not an instant.
 *
 * There are two of them — a month and an ISO week — and both are named by
 * the DATE THEY START ON: "2026-08-01" and "2026-08-17". That is why a week
 * needs no second column: one string identifies
 * an occurrence under any frequency, and it still sorts.
 *
 * Every date in this app is a local calendar string — "2026-08" for a billing
 * period, "2026-08-05" for a due date — and never a Date carried across a
 * boundary. That is deliberate: `new Date("2026-08-05")` parses as MIDNIGHT
 * UTC, which in Panama (UTC-5) is the 4th at 19:00, and an obligation that
 * silently falls due a day early is the kind of bug that only shows up in
 * front of a customer.
 *
 * Comparisons are lexicographic on these strings, which is exact for
 * zero-padded ISO dates and needs no timezone at all.
 */
import { activeTimezone, offsetMinutesAt } from "./timezone.js";
const MONTHS = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
/**
 * The same twelve, abbreviated, for a control that has no room for a
 * sentence -- see `formatShortDay`. No trailing dots: they are one more mark
 * to read inside a button, and the three parts of a date in order are not
 * ambiguous without them.
 */
const MONTHS_SHORT = [
    "ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sept", "oct", "nov", "dic",
];
/** ISO order: 1 is Monday, 7 is Sunday, which is what `due_dow` stores. */
const DAYS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
function pad(n) {
    return String(n).padStart(2, "0");
}
/** The period a date belongs to: "2026-08-05" → "2026-08". */
export function periodOf(date) {
    return date.slice(0, 7);
}
/** `shiftPeriod("2026-01", -1)` → `"2025-12"`. */
export function shiftPeriod(period, months) {
    const [year, month] = splitPeriod(period);
    const zeroBased = year * 12 + (month - 1) + months;
    return `${Math.floor(zeroBased / 12)}-${pad((zeroBased % 12) + 1)}`;
}
function splitPeriod(period) {
    const year = Number(period.slice(0, 4));
    const month = Number(period.slice(5, 7));
    return [year, month];
}
/** "2026-08" → "Agosto 2026" */
export function periodLabel(period) {
    const [year, month] = splitPeriod(period);
    return `${MONTHS[month - 1]} ${year}`;
}
/**
 * "2026-09" → "Sept 2026" -- `periodLabel` for a switcher with no room for
 * «Septiembre» (Pedidos' month view beside Día|Mes).
 * `MONTHS_SHORT`, capitalised as the start of the label.
 */
export function shortPeriodLabel(period) {
    const [year, month] = splitPeriod(period);
    const name = MONTHS_SHORT[month - 1] ?? "";
    return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}
/** "2026-08" → "Agosto" */
export function periodMonthLabel(period) {
    const [, month] = splitPeriod(period);
    return MONTHS[month - 1] ?? "";
}
/** "2026-08" → "agosto", for sentences: "Mensualidad de agosto". */
export function periodMonthLower(period) {
    return periodMonthLabel(period).toLowerCase();
}
/** Days in a month, leap years included. */
export function daysInMonth(period) {
    const [year, month] = splitPeriod(period);
    // Day 0 of the next month is the last day of this one.
    return new Date(year, month, 0).getDate();
}
/**
 * The cells of a month's calendar page, in rows of seven starting on Monday
 * -- como se imprime cualquier calendario en Panama, and the same convention
 * the web's own calendar uses (`weekStartsOn={1}`). `null` fills the gap
 * before day 1 and after the month's last day, so the length is always a
 * multiple of seven and a caller can chunk it straight into complete weeks.
 */
export function monthGrid(period) {
    const total = daysInMonth(period);
    // Parsed at UTC, like `weekStartOf`, so no device timezone can shift which
    // weekday day 1 lands on.
    const sunday0 = new Date(Date.parse(`${period}-01T00:00:00Z`)).getUTCDay();
    const isoDow = sunday0 === 0 ? 7 : sunday0; // 1 = Monday ... 7 = Sunday
    const leading = isoDow - 1;
    const days = Array.from({ length: total }, (_, i) => `${period}-${pad(i + 1)}`);
    const cells = [...Array.from({ length: leading }).fill(null), ...days];
    const trailing = (7 - (cells.length % 7)) % 7;
    return [...cells, ...Array.from({ length: trailing }).fill(null)];
}
/**
 * The due date for a period, with the day clamped to the month.
 *
 * A plan that falls due on the 31st has no 31st in February, and the answer
 * that never surprises anyone is the last day of the month.
 */
export function dueDateFor(period, dueDay) {
    const clamped = Math.min(Math.max(Math.trunc(dueDay), 1), daysInMonth(period));
    return `${period}-${pad(clamped)}`;
}
/** Whole days from `from` to `to`, positive when `to` is later. */
export function daysBetween(from, to) {
    const day = 24 * 60 * 60 * 1000;
    // Parsed at UTC midnight on BOTH sides, so the offset cancels and no
    // daylight-saving hour can round the difference the wrong way.
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / day);
}
/**
 * Calendar arithmetic on a date string, done in UTC on both ends so the
 * offset cancels: "2026-02-27" + 3 days is "2026-03-02" in Panama and in a
 * simulator set to Tokyo.
 */
export function addDays(date, days) {
    const shifted = new Date(Date.parse(`${date}T00:00:00Z`) + days * 24 * 60 * 60 * 1000);
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}
/**
 * The same date, so many months later -- "2026-09-15" + 6 is "2027-03-15".
 *
 * A MONTH IS NOT THIRTY DAYS, which is the whole reason this exists beside
 * `addDays`: «dentro de seis meses» said in days lands on a different day of
 * the month every time it is asked, and somebody agreeing a maintenance visit
 * on the 15th means the 15th.
 *
 * THE DAY IS CLAMPED TO THE MONTH IT LANDS IN, the same answer `dueDateFor`
 * gives a plan that falls due on the 31st: there is no 31 February, and the
 * last day of the month is the one that surprises nobody. Arithmetic on the
 * three numbers rather than on a `Date`, so no device timezone can move the
 * result by a day -- the rule this whole file follows.
 */
export function addMonths(date, months) {
    const day = Number(date.slice(8, 10));
    // Months since year 0, so a negative `months` crosses a year end without a
    // branch of its own.
    const total = Number(date.slice(0, 4)) * 12 + (Number(date.slice(5, 7)) - 1) + months;
    const year = Math.floor(total / 12);
    const period = `${year}-${pad(total - year * 12 + 1)}`;
    return `${period}-${pad(Math.min(day, daysInMonth(period)))}`;
}
/** The last calendar date of a period: "2026-02" → "2026-02-28". */
export function lastDayOf(period) {
    return `${period}-${pad(daysInMonth(period))}`;
}
/** The first and last day a whole period covers. */
export function wholePeriodRange(period) {
    return period.kind === "MONTH"
        ? { from: `${period.month}-01`, to: lastDayOf(period.month) }
        : { from: `${String(period.year)}-01-01`, to: `${String(period.year)}-12-31` };
}
/** The whole month or year a range is exactly, or null when it is anything else. */
export function wholePeriodOf(range) {
    const month = periodOf(range.from);
    if (range.from === `${month}-01` && range.to === lastDayOf(month))
        return { kind: "MONTH", month };
    const year = range.from.slice(0, 4);
    if (range.from === `${year}-01-01` && range.to === `${year}-12-31`)
        return { kind: "YEAR", year: Number(year) };
    return null;
}
/** «Septiembre 2026», or «2026». */
export function wholePeriodLabel(period) {
    return period.kind === "MONTH" ? periodLabel(period.month) : String(period.year);
}
/** The shortcuts of a month-granularity control: this and last month, this and last year, each whole. */
export function wholePeriodPresets(today) {
    const month = periodOf(today);
    const year = Number(today.slice(0, 4));
    return [
        { label: "Este mes", range: wholePeriodRange({ kind: "MONTH", month }) },
        { label: "Mes pasado", range: wholePeriodRange({ kind: "MONTH", month: shiftPeriod(month, -1) }) },
        { label: "Este año", range: wholePeriodRange({ kind: "YEAR", year }) },
        { label: "Año pasado", range: wholePeriodRange({ kind: "YEAR", year: year - 1 }) },
    ];
}
/**
 * The date range for one of `PeriodRangeKind`'s three choices, computed off
 * `today` alone -- never a device-local `Date` crossing midnight, which is
 * exactly the bug this app already shipped once (midnight UTC lands on the
 * PREVIOUS day in Panama). `shiftPeriod`/`lastDayOf`/`periodOf` are the only
 * arithmetic here, the same ones every other function in this file uses, so
 * a quarter or a year-end can never disagree with how a month already works.
 *
 * `MONTH` AND `QUARTER` ARE THE WHOLE PERIOD, start to its own last day, the
 * same as `presetRange`'s own `THIS_MONTH` on the web -- there being no data
 * beyond today, asking for the rest of the month or quarter changes nothing
 * about what comes back.
 *
 * `YEAR_TO_DATE` STOPS AT `today` INSTEAD, and only this one: that is what
 * "corrido" promises in its own name, unlike the other two, and it is also
 * the one range long enough that stopping early keeps every request
 * comfortably under `MAX_REPORT_SPAN_DAYS` regardless of the day it is asked
 * on.
 *
 * THE QUARTER'S OWN MATH: back up to whichever of the year's four
 * three-month blocks `today` falls in (Ene-Mar, Abr-Jun, Jul-Sep, Oct-Dic),
 * via `(month - 1) % 3` months of `shiftPeriod`, then two more forward for
 * its last month -- the same "day 0 of the next one" trick `lastDayOf`
 * already uses for a month's last day, just three periods wide.
 */
export function periodRangeOf(kind, today) {
    const period = periodOf(today);
    if (kind === "MONTH")
        return { from: `${period}-01`, to: lastDayOf(period) };
    if (kind === "YEAR_TO_DATE")
        return { from: `${period.slice(0, 4)}-01-01`, to: today };
    const month = Number(period.slice(5, 7));
    const quarterStart = shiftPeriod(period, -((month - 1) % 3));
    return { from: `${quarterStart}-01`, to: lastDayOf(shiftPeriod(quarterStart, 2)) };
}
/** Keeps a date inside a range; both bounds are inclusive. */
export function clampDate(date, min, max) {
    if (date < min)
        return min;
    if (date > max)
        return max;
    return date;
}
/**
 * `periodRangeOf`'s range and its homologous range one year back, clipped to
 * the SAME NUMBER OF ELAPSED DAYS on both sides.
 *
 * THE CLIPPING IS THE WHOLE POINT. `periodRangeOf("MONTH", …)` returns the
 * whole month, future days included, because there being no data beyond
 * today changes nothing about a range that only describes ITSELF. But a
 * year-over-year comparison reads two ranges side by side, and 16 days of
 * this September next to all 30 of last September is not a fair comparison
 * that happens to use two different day counts -- it is the exact "comparar
 * 16 días contra 30 y llamarlo una caída" the spec calls out by name. So
 * `current.to` is clamped to `today`, and `previous.to` is clamped to the
 * SAME distance from `previous.from`, by shifting `current.to` back a year
 * rather than re-deriving it -- the two ranges can then only ever cover an
 * equal span.
 *
 * `addMonths(…, -12)` DOES THE YEAR SHIFT, not hand-rolled year arithmetic:
 * it already clamps a day that does not exist in the target month, which is
 * what keeps a leap day's homologous range at February 28 the year before
 * instead of overflowing into March or landing short by a day.
 */
export function yearOverYearRangesFor(kind, today) {
    const whole = periodRangeOf(kind, today);
    const to = clampDate(whole.to, whole.from, today);
    return {
        current: { from: whole.from, to },
        previous: { from: addMonths(whole.from, -12), to: addMonths(to, -12) },
        complete: to === whole.to,
    };
}
/** ISO weekday of a calendar day: 1 = Monday … 7 = Sunday, as `app.work_hours` and `app.business_hours` store it. */
export function isoWeekdayOf(day) {
    const sunday0 = new Date(Date.parse(`${day}T00:00:00Z`)).getUTCDay();
    return sunday0 === 0 ? 7 : sunday0;
}
/** "2026-08-05" → "5 de agosto" */
export function formatDayMonth(date) {
    const day = Number(date.slice(8, 10));
    return `${day} de ${periodMonthLower(periodOf(date))}`;
}
/** "2026-09-01" → "1 de septiembre de 2026". The whole date, spelled out. */
export function formatFullDay(date) {
    return `${formatDayMonth(date)} de ${date.slice(0, 4)}`;
}
/**
 * "2026-09-01" → "1 sept 2026". The same date for a CONTROL rather than a
 * sentence: the web's date picker draws it inside its own button, where «1 de
 * septiembre de 2026» wraps and pushes the row around.
 *
 * `formatFullDay` stays exactly as it is -- it is read inside sentences
 * («Registrado por María el 1 de septiembre de 2026») and in the CSV, where
 * the long month is the right one.
 */
export function formatShortDay(date) {
    const [, month] = splitPeriod(periodOf(date));
    return `${Number(date.slice(8, 10))} ${MONTHS_SHORT[month - 1]} ${date.slice(0, 4)}`;
}
/**
 * "2026-09-09" → "9 sept" inside the current year, "31 dic 2025" outside it.
 *
 * For a control in a TIGHT row -- the web's day arrows sit beside «Mes |
 * Día», and the four-part date pushed that row into wrapping on a
 * phone-width window. The year of the year you are already in adds width and
 * no information; the year of any other one is exactly what you need to see.
 */
export function formatCompactDay(date, today = todayIn()) {
    const full = formatShortDay(date);
    return date.slice(0, 4) === today.slice(0, 4) ? full.slice(0, full.lastIndexOf(" ")) : full;
}
// ─── The business's clock (spec 2026-09-25 §2.2) ────────────────────────────
// Every date and time a user reads or picks is computed in the BUSINESS'S zone.
// The device only contributes the instant (`now`), never the zone; the server
// never assumes one -- it receives it. `tz` defaults to the active zone, which
// a client sets from the business profile (timezone.ts) exactly as it sets the
// currency.
/** The instant `at` shifted into the zone's wall clock, read with UTC accessors. */
function wallClock(at, tz) {
    const instant = new Date(at);
    return new Date(instant.getTime() + offsetMinutesAt(tz, instant) * 60_000);
}
/**
 * The day an instant falls on in the business's zone.
 *
 * NEVER A `Timestamp`'s FIRST TEN CHARACTERS: those are its UTC day, and 20:00
 * in Panama is already tomorrow in UTC -- a movement recorded that evening
 * would read as recorded tomorrow. This is the only way from an instant to a day.
 */
export function dayOf(at, tz = activeTimezone()) {
    return wallClock(at, tz).toISOString().slice(0, 10);
}
/** The "HH:MM" the business's wall clock shows for an instant -- the pair to `dayOf`, used to split a `dueAt` back into what a form asks in two pieces. */
export function timeOf(at, tz = activeTimezone()) {
    return wallClock(at, tz).toISOString().slice(11, 16);
}
function offsetSuffix(minutes) {
    const sign = minutes < 0 ? "-" : "+";
    const abs = Math.abs(minutes);
    return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
/**
 * The inverse of `dayOf` and `timeOf` together: the instant a wall-clock
 * `date` + `time` names in `tz`, written with that zone's offset
 * (`…T09:30:00-05:00` in Panama, for every Panama business the same string it
 * always was).
 *
 * THE TWO HOURS A YEAR THAT ARE NOT ONE INSTANT, resolved like Temporal's
 * "compatible": a time the spring change skips (02:30 on 8 Mar 2026 in New
 * York) is read with the offset before the jump, so it lands one hour later on
 * the clock (03:30); a time the autumn change repeats is its FIRST occurrence.
 * The offsets a day either side are the only two candidates -- changes are
 * months apart -- and the first one whose instant keeps it wins.
 */
export function instantOf(date, time, tz = activeTimezone()) {
    const naive = Date.parse(`${date}T${time}:00Z`);
    const before = offsetMinutesAt(tz, new Date(naive - 86_400_000));
    const after = offsetMinutesAt(tz, new Date(naive + 86_400_000));
    const offset = [before, after].find((candidate) => offsetMinutesAt(tz, new Date(naive - candidate * 60_000)) === candidate) ?? before;
    return `${date}T${time}:00${offsetSuffix(offset)}`;
}
/** Today in the business's zone. `now` is the only thing the device contributes. */
export function todayIn(tz = activeTimezone(), now = new Date()) {
    return dayOf(now.toISOString(), tz);
}
/** The current billing month in the business's zone. */
export function periodIn(tz = activeTimezone(), now = new Date()) {
    return todayIn(tz, now).slice(0, 7);
}
/** The Monday of the current week in the business's zone. */
export function weekStartIn(tz = activeTimezone(), now = new Date()) {
    return weekStartOf(todayIn(tz, now));
}
/** Whether a string is a real "HH:MM" answer, the shape `checkCustomOrderForm` and the server's own schema both check. */
export function isTimeOfDay(time) {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
}
/** Thirty minutes forward or back, wrapping across midnight -- the step `TimeStepper`'s arrows take. */
export function shiftTime(time, minutes) {
    const [hour = 0, minute = 0] = time.split(":").map(Number);
    const total = (((hour * 60 + minute + minutes) % 1440) + 1440) % 1440;
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}
/**
 * «3:00 p.m.» from a 24-hour "HH:MM" -- the two places everybody gets this
 * wrong: mediodía reads as "12:00 m.d.", never "12:00 p.m." -- and only noon
 * sharp, so 12:35 is "12:35 p.m." --, and medianoche as "12:00 a.m.", never "0:00".
 */
export function formatTimeOfDay(time) {
    const [hour = 0, minute = 0] = time.split(":").map(Number);
    const mm = pad(minute);
    if (hour === 0)
        return `12:${mm} a.m.`;
    if (hour === 12)
        return minute === 0 ? "12:00 m.d." : `12:${mm} p.m.`;
    return `${hour % 12}:${mm} ${hour < 12 ? "a.m." : "p.m."}`;
}
/** «5 de septiembre a las 3:00 p.m.» -- the day and the hour the business's own zone reads, never the device's. */
export function formatWhen(at, tz = activeTimezone()) {
    return `${formatDayMonth(dayOf(at, tz))} a las ${formatTimeOfDay(timeOf(at, tz))}`;
}
/**
 * How a payment date reads in a list: "Hoy", "Ayer", or the date itself.
 * Recent payments are almost always one of the first two, and naming the day
 * beats making someone subtract dates in their head.
 */
export function formatRelativeDay(date, today) {
    const delta = daysBetween(date, today);
    if (delta === 0)
        return "Hoy";
    if (delta === 1)
        return "Ayer";
    return formatDayMonth(date);
}
/**
 * The same "Hoy"/"Ayer" rule as `formatRelativeDay`, for a TABLE COLUMN
 * rather than a sentence -- the web's Libro, Cobros and Autos, where "10 de
 * septiembre" is wider than the column needs.
 * Everything past "Ayer" reads through `formatCompactDay` instead of
 * `formatDayMonth`. Left as its own function rather than a parameter on
 * `formatRelativeDay`: the agenda's own day title still reads through that
 * one -- it is a TITLE with a line to itself,
 * where the long month costs nothing.
 *
 * THE PHONE'S `DateStepper` READS THIS TOO, «23 sept»: it is a narrow
 * control with two arrows, and «1 de septiembre» would break into two lines
 * inside the pill.
 */
export function formatRelativeCompactDay(date, today) {
    const delta = daysBetween(date, today);
    if (delta === 0)
        return "Hoy";
    if (delta === 1)
        return "Ayer";
    return formatCompactDay(date, today);
}
/**
 * The heading over a day's entries in a ledger grouped by day: «Hoy»,
 * «Ayer», and from there the weekday before the date — «lunes 1 de
 * septiembre» — because a person placing last week's expense remembers
 * "the Monday" before they remember "the 1st".
 *
 * ALSO NAMES TOMORROW, as «Mañana». The ledger this was written for never
 * needed it — its date is capped at today — but a caller listing pedidos
 * does: whoever bakes cares about tomorrow about as much as today, and
 * making them read a weekday to find the second thing on their list defeats
 * the point of naming days at all. One function names a day relative to
 * today for every screen in the app, on purpose — two would be two chances
 * for "Hoy" to mean a different date on the phone than on the Libro.
 */
export function formatDayHeading(date, today) {
    const delta = daysBetween(date, today);
    if (delta === 0)
        return "Hoy";
    if (delta === 1)
        return "Ayer";
    if (delta === -1)
        return "Mañana";
    // JS gives 0 = Sunday; `dowLabel` takes ISO 1 = Monday … 7 = Sunday.
    const jsDay = new Date(Date.parse(`${date}T00:00:00Z`)).getUTCDay();
    return `${dowLabel(jsDay === 0 ? 7 : jsDay)} ${formatDayMonth(date)}`;
}
/** The last `count` periods ending at `period`, oldest first. */
export function recentPeriods(period, count) {
    return Array.from({ length: count }, (_, i) => shiftPeriod(period, i - (count - 1)));
}
// ------------------------------------------------------------------- weeks
/**
 * The Monday of the ISO week a date falls in.
 *
 * MONDAY FOR EVERYBODY, and not the day each customer enrolled on. With weeks
 * anchored per customer, "the week of the 17th" means something different for
 * every family and Cobros' week selector cannot exist — there is no one week to
 * show. Postgres already agrees: `date_trunc('week', ...)` returns the Monday,
 * which is what lets the generator and this function produce the same answer.
 *
 * Parsed at UTC on both ends, like `addDays`, so the offset cancels and no
 * device timezone can move a Monday onto a Sunday.
 */
export function weekStartOf(date) {
    const sunday0 = new Date(Date.parse(`${date}T00:00:00Z`)).getUTCDay();
    // getUTCDay is 0..6 with Sunday at 0; ISO is 1..7 with Monday at 1.
    const iso = sunday0 === 0 ? 7 : sunday0;
    return addDays(date, 1 - iso);
}
/** The Sunday that closes the week starting on `weekStart`. */
export function weekEndOf(weekStart) {
    return addDays(weekStart, 6);
}
/** `shiftWeek("2026-08-31", 1)` → `"2026-09-07"`. */
export function shiftWeek(weekStart, weeks) {
    return addDays(weekStart, weeks * 7);
}
/** The last `count` weeks ending at `weekStart`, oldest first. */
export function recentWeeks(weekStart, count) {
    return Array.from({ length: count }, (_, i) => shiftWeek(weekStart, i - (count - 1)));
}
/**
 * "17 al 23 de agosto", or "31 de agosto al 6 de septiembre" when the range
 * crosses a month -- "del 31 al 6 de septiembre" reads as an error. Any pair
 * of dates, not only a Monday-Sunday week: a chart column clipped to a
 * calendar boundary (Analíticas' own month-in-weeks chart, see
 * `weekBucketTitle`) needs the same wording for whatever days it actually
 * covers.
 */
export function dayRangeLabel(from, to) {
    const fromDay = Number(from.slice(8, 10));
    const toDay = Number(to.slice(8, 10));
    const fromMonth = periodMonthLower(periodOf(from));
    const toMonth = periodMonthLower(periodOf(to));
    return fromMonth === toMonth ? `${fromDay} al ${toDay} de ${toMonth}` : `${fromDay} de ${fromMonth} al ${toDay} de ${toMonth}`;
}
/**
 * «1 sept – 7 sept», the days a chart column actually covers.
 *
 * NOT «Sem 1» UNDER THE BARS: an ordinal only says which column you are
 * looking at, which its position already said; the days say what is IN it.
 * It matters most for the first and last columns of a month, which are the
 * two that are almost never seven days long -- a bar that looks small because
 * it covers two days reads as a bad week until you know that.
 *
 * THE MONTH ON BOTH ENDS, even when it is the same one. It
 * costs four characters and it removes the one question the bare «1–7» leaves
 * open on a chart that can be showing a month, a quarter or a year.
 *
 * `MONTHS_SHORT` AND NOT A SECOND SPELLING: «sept» is what every other
 * abbreviated month in this app already says.
 *
 * SHORT ON PURPOSE. `dayRangeLabel` above is the sentence («1 al 6 de
 * septiembre») and carries the hover and the screen reader; this is
 * what fits under a column that may be one of five on a phone.
 *
 * AND IT COMES IN TWO LINES, with the break between the two ends. One line
 * does not fit: a full month is FIVE week columns, which on a 402pt phone
 * leaves about 63pt each, and «1 sept – 6 sept» needs 93. On one line every
 * column ellipsises -- «1 sept –…», «14 sept…» -- and not one week says where
 * it ends, which is precisely the question this label answers.
 *
 * THE BREAK IS PART OF THE LABEL, not something a client decides: both
 * clients draw the same string, and a chart that wrapped differently on the
 * phone and the web would be two labels again. Chosen over the alternatives
 * because it keeps every word -- the month stays on BOTH ends, the rule the
 * shorter options all had to break.
 */
export function shortDayRange(from, to) {
    return `${shortDayMonth(from)}\n\u2013 ${shortDayMonth(to)}`;
}
/**
 * One column of the Estado de resultados: a whole month reads as the month
 * («Oct 2026», as `shortPeriodLabel` says it), and a slice clipped by the
 * range reads as its days («18–30 sept»). Both ends are always in one month.
 */
export function monthSliceLabel(from, to) {
    if (from.endsWith("-01") && to === lastDayOf(periodOf(to)))
        return shortPeriodLabel(periodOf(from));
    if (from === to)
        return shortDayMonth(from);
    return `${Number(from.slice(8, 10))}\u2013${shortDayMonth(to)}`;
}
/** "2026-09-28" → "28 sept": one end of `shortDayRange`, and of a planilla's days in the Libro (`runConcept`). */
export function shortDayMonth(date) {
    return `${Number(date.slice(8, 10))} ${MONTHS_SHORT[Number(date.slice(5, 7)) - 1]}`;
}
/** "Semana del 17 al 23 de agosto", and never "Semana 34" -- the ISO week number means nothing to the owner of a bus route. */
export function weekLabel(weekStart) {
    return `Semana del ${dayRangeLabel(weekStart, weekEndOf(weekStart))}`;
}
/**
 * What a cobro's period is called, given how its plan bills.
 *
 * SEPARATE FROM `periodLabel`, deliberately. That one answers "what is this
 * month called" and is what both month selectors ask; this one answers "what is
 * this cobro's period called". One name for two questions would make every call
 * site prove which of them it was asking.
 */
export function obligationPeriodLabel(periodStart, frequency) {
    return frequency === "WEEKLY" ? weekLabel(periodStart) : periodLabel(periodOf(periodStart));
}
/** The period a date falls in: the 1st of its month, or the Monday of its week. */
export function periodStartOf(date, frequency) {
    return frequency === "WEEKLY" ? weekStartOf(date) : `${periodOf(date)}-01`;
}
/**
 * The twin of the `case pl.frequency` in `generate()`, for the paths that
 * create an obligation in this process rather than in one statement:
 * `createCustomer` and the demo seed.
 *
 * Monthly keeps the clamp it always had — a plan due on the 31st falls due on
 * the 28th of a February. Weekly needs none: every week has all seven days.
 */
export function dueDateForPeriod(periodStart, frequency, dueDay, dueDow) {
    if (frequency === "WEEKLY") {
        const clamped = Math.min(Math.max(Math.trunc(dueDow ?? 1), 1), 7);
        return addDays(periodStart, clamped - 1);
    }
    return dueDateFor(periodOf(periodStart), dueDay ?? 1);
}
/** "5" → "viernes". The number is storage; this is what a person reads. */
export function dowLabel(dow) {
    return DAYS[Math.min(Math.max(Math.trunc(dow), 1), 7) - 1] ?? "";
}
/** The seven days, in ISO order, for a picker. */
export const DOW_LABELS = DAYS.map((label, index) => ({ value: index + 1, label }));
/**
 * How a plan's terms read: «B/.12.00 a la semana · vence los viernes».
 *
 * The money is left out, because `formatMoney` needs a currency symbol and that
 * is app identity rather than domain — the reason it never moved into this
 * package. What is here is the half that must not differ between a browser and
 * a phone: "al mes" against "a la semana", and a due day written the way the
 * owner said it rather than as the number underneath.
 */
export function planTerms(plan) {
    const months = billingMonthsLabel(plan.billingMonths ?? ALL_MONTHS);
    return plan.frequency === "WEEKLY"
        ? { cadence: "a la semana", due: `vence los ${dowLabel(plan.dueDow ?? 1)}`, months }
        : { cadence: "al mes", due: `vence el día ${plan.dueDay ?? 1}`, months };
}
/** The twelve months, January = 1: a plan that bills all year, and the default. */
export const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
/** «marzo». */
export function monthName(month) {
    return (MONTHS[month - 1] ?? "").toLowerCase();
}
/**
 * WHETHER A PERIOD BILLS (a tutoring centre teaches March to December). The period's month -- the 1st for a monthly plan, the Monday
 * that opens the week for a weekly one. The generator's SQL transcribes this
 * (`extract(month from g.starts_at) = any(pl.billing_months)`), and
 * `billing-months.test.ts` holds the two together month by month.
 */
export function billsInPeriod(billingMonths, periodStart) {
    return billingMonths.includes(Number(periodStart.slice(5, 7)));
}
/** The first marked month from this period's on, going round the year. */
export function nextBillingMonth(billingMonths, periodStart) {
    const from = Number(periodStart.slice(5, 7));
    for (let step = 0; step < 12; step += 1) {
        const month = ((from - 1 + step) % 12) + 1;
        if (billingMonths.includes(month))
            return month;
    }
    return from;
}
/**
 * «de marzo a diciembre» when the marked months run on (round the year too),
 * the short list otherwise, nothing with all twelve -- what a plan's line
 * says under its price.
 */
export function billingMonthsLabel(billingMonths) {
    const marked = [...new Set(billingMonths)].filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
    if (marked.length === 0 || marked.length === 12)
        return "";
    // A run that wraps starts at the first marked month whose previous one is not.
    const startIndex = Math.max(0, marked.findIndex((m) => !marked.includes(m === 1 ? 12 : m - 1)));
    const ordered = [...marked.slice(startIndex), ...marked.slice(0, startIndex)];
    const contiguous = ordered.every((m, i) => i === 0 || m === (ordered[i - 1] % 12) + 1);
    if (contiguous && ordered.length > 1)
        return `de ${monthName(ordered[0])} a ${monthName(ordered.at(-1))}`;
    return marked.map((m) => MONTHS_SHORT[m - 1]).join(", ");
}
/**
 * A day typed by hand, the way Panama writes it: DD/MM/AAAA (a dash also
 * works, and so do the eight digits alone). A birth date is decades back, where a month-by-month calendar is
 * hundreds of taps, and the phone has no native picker (no native
 * dependencies). Null for anything that is not a real calendar day.
 */
export function parseDayInput(text) {
    // EIGHT BARE DIGITS ARE DDMMAAAA too: the web's numeric keypad on an iPhone
    // has no «/» and no «-», and without this a date could not be typed there.
    const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(text.trim()) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(text.trim());
    if (!match)
        return null;
    const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (month < 1 || month > 12 || day < 1)
        return null;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day > last)
        return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
/** An IsoDate as the field shows it: DD/MM/AAAA, or empty. */
export function dayInputOf(date) {
    if (date === null)
        return "";
    return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}
